import { create } from "zustand";

type VoiceState = {
  isRecording: boolean;
  isCapturing: boolean;
  isTranscribing: boolean;
  isSessionActive: boolean;
  isVoiceActive: boolean;
  isSpeaking: boolean;
  autoReadEnabled: boolean;
  autoListenEnabled: boolean;
  setRecording: (value: boolean) => void;
  setCapturing: (value: boolean) => void;
  setTranscribing: (value: boolean) => void;
  setSessionActive: (value: boolean) => void;
  setVoiceActive: (value: boolean) => void;
  setSpeaking: (value: boolean) => void;
  setAutoReadEnabled: (value: boolean) => void;
  setAutoListenEnabled: (value: boolean) => void;
};

/**
 * Cross-component voice flags.
 *  - isRecording: mic is engaged at all (session active, listening for voice,
 *    or actually capturing). Drives the Send button's disabled state.
 *  - isCapturing: MediaRecorder is *actually* writing audio chunks (post-VAD
 *    trigger, pre-stop). Drives the "Listening…" card so it stays visible
 *    through mid-utterance pauses, but stays hidden during the silent
 *    waiting-for-voice phase.
 *  - isTranscribing: mic stopped, backend STT in flight.
 *  - isSessionActive: hands-free listening mode — mic re-arms after each utterance.
 *  - isSpeaking: TTS is currently playing (holds the mic off during session).
 *  - autoReadEnabled: assistant replies should auto-play via TTS.
 *  - autoListenEnabled: mic click enters continuous session mode (re-arms after
 *    each utterance) instead of single-shot. Default off.
 */
export const useVoiceStore = create<VoiceState>((set) => ({
  isRecording: false,
  isCapturing: false,
  isTranscribing: false,
  isSessionActive: false,
  isVoiceActive: false,
  isSpeaking: false,
  autoReadEnabled: false,
  autoListenEnabled: false,
  setRecording: (value) => set({ isRecording: value }),
  setCapturing: (value) => set({ isCapturing: value }),
  setTranscribing: (value) => set({ isTranscribing: value }),
  setSessionActive: (value) => set({ isSessionActive: value }),
  setVoiceActive: (value) => set({ isVoiceActive: value }),
  setSpeaking: (value) => set({ isSpeaking: value }),
  setAutoReadEnabled: (value) => set({ autoReadEnabled: value }),
  setAutoListenEnabled: (value) => set({ autoListenEnabled: value }),
}));
